import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useMemoize } from '@vueuse/core'
import volunteerCooperationApi from '@/services/http/api/volunteerCooperation'
import { VolunteerCooperationResponseSchema } from '@/schemas/volunteerCooperation'
import type {
  VolunteerCooperationData,
  ChildEditionId,
  VolunteerCooperationChangelog
} from '@/types'

export const useVolunteerCooperationStore = defineStore('volunteerCooperationStore', () => {
  // state
  const fetchLoader = ref(false)
  const toggleLoader = ref(false)
  const volunteerCooperationData = ref<VolunteerCooperationData | null>(null)

  // getters
  const volunteerCooperationMark = computed<boolean>(
    () => volunteerCooperationData.value?.volunteer_cooperation_mark ?? false
  )
  const volunteerCooperationChangelogs = computed<VolunteerCooperationChangelog[]>(
    () => volunteerCooperationData.value?.volunteer_cooperation_changelogs ?? []
  )

  // Memoize only the API call
  const memoizedGetVolunteerCooperation = useMemoize(volunteerCooperationApi.get)

  const fetchVolunteerCooperation = async (childEditionId: ChildEditionId) => {
    fetchLoader.value = true
    try {
      const { data } = await memoizedGetVolunteerCooperation(childEditionId)
      const validated = VolunteerCooperationResponseSchema.parse(data)
      volunteerCooperationData.value = validated.data
      return validated.data
    } finally {
      fetchLoader.value = false
    }
  }

  const toggleVolunteerCooperation = async (childEditionId: ChildEditionId) => {
    toggleLoader.value = true
    try {
      const { data } = await volunteerCooperationApi.toggle(childEditionId)
      const validated = VolunteerCooperationResponseSchema.parse(data)
      volunteerCooperationData.value = validated.data
      // Clear memoized fetch cache since data changed
      memoizedGetVolunteerCooperation.clear()
      return validated.data
    } finally {
      toggleLoader.value = false
    }
  }

  const clear = () => {
    volunteerCooperationData.value = null
    memoizedGetVolunteerCooperation.clear()
  }

  return {
    // state
    fetchLoader,
    toggleLoader,
    volunteerCooperationData,

    // getters
    volunteerCooperationMark,
    volunteerCooperationChangelogs,

    // actions
    fetchVolunteerCooperation,
    toggleVolunteerCooperation,
    clear
  }
})
